import { useNavigate, useLocation } from 'react-router-dom';
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import { useGoogleLogin } from '@react-oauth/google';
import server from '../api/server.js';
import { useState, useEffect } from 'react';
import '../dashboard-components/loader.css';
import ManualLogin from './ManualLogin.jsx';
import { HiOutlineMail } from "react-icons/hi";
import { TiUserAdd } from "react-icons/ti";
import CreateNewLogin from "../dashboard-components/login/CreateNewLogin.jsx"
import { useToast } from '../context/ToastContext.jsx';
import { setAccessTokenExpiresAt, scheduleAccessTokenRefresh } from '../utils/dashboardAuth';
import {
  clearStoredLoginRedirect,
  getSafeLoginRedirect,
  persistLoginRedirect,
} from '../utils/loginRedirect';

function msRedirectUri() {
  return process.env.REACT_APP_MICROSOFT_REDIRECT_URI;
}
function msAuthority() {
  return 'common';
}
function msStripOAuthParams(search) {
  const p = new URLSearchParams(search);
  ['code', 'state', 'session_state'].forEach((k) => p.delete(k));
  const s = p.toString();
  return s ? `?${s}` : '';
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [texts] = useState(["Call Audit", "Lead Quality", "Customer Insights", "Churn Reduction", "Marketing Insights"]);
  const [displayText, setDisplayText] = useState('');
  const [index, setIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [showLogin, setShowLogin] = useState(false);
  const [showCreateNewLogin, setShowCreateNewLogin] = useState(false)
  const microsoftClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
  const handleLoginStatus = async (e, f) => {
    setShowLogin(e);
    if(f){
      await handleApiCheck(f.info, f.services, f.session_id, f.access_token_expires_at);
    }
  }

  useEffect(() => {
    const currentText = texts[index];

    const typingEffect = () => {
      if (isDeleting) {
        if (charIndex > 0) {
          const timer = setTimeout(() => {
            setDisplayText(currentText.substring(0, charIndex - 1));
            setCharIndex(charIndex - 1);
          }, 100); // Speed of deleting
          return () => clearTimeout(timer);
        } else {
          setIsDeleting(false);
          const timer = setTimeout(() => {
            setIndex((index + 1) % texts.length); // Move to the next text
            setCharIndex(0); // Reset character index
          }, 100); // Wait before starting to type the next text
          return () => clearTimeout(timer);
        }
      } else {
        if (charIndex < currentText.length) {
          const timer = setTimeout(() => {
            setDisplayText(currentText.substring(0, charIndex + 1));
            setCharIndex(charIndex + 1);
          }, 100); // Speed of typing
          return () => clearTimeout(timer);
        } else {
          const timer = setTimeout(() => {
            setIsDeleting(true); // Start deleting after typing complete
          }, 500); // Wait before starting to delete
          return () => clearTimeout(timer);
        }
      }
    };

    typingEffect(); // Call the typing effect function

  }, [charIndex, isDeleting, index, texts]);

  // Get the intended destination URL from query parameters
  const getIntendedDestination = () => {
    return getSafeLoginRedirect(location.search);
  };

  const handleApiCheck = async(info, services, session_id, access_token_expires_at) => {

      if (info.client_id && info.email) {
        let userData = null;
        
        userData = {
            email: info.email,
            client_id: info.client_id,
            service_id: info.service_id,
            loginTime: new Date().getTime(),
            role: info.role,
            name: info.name,
            'session_id': session_id
          };
        userData['services'] = services
        // Save user session data in cookies (expire in 12 hours)
        const currentDate = new Date();

        // Set the expiry date to 30 days from now
        currentDate.setTime(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));

        // Format the expiry date in UTC format
        const expires = `expires=${currentDate.toUTCString()}`;

        // Set the cookie with the expiration time
        document.cookie = `dashboard_login_user_creds=${JSON.stringify(userData)}; ${expires}; path=/; Secure; SameSite=None`;
        
        if (access_token_expires_at) {
          setAccessTokenExpiresAt(access_token_expires_at);
          scheduleAccessTokenRefresh(server);
        }
        
        // Check if there's an intended destination URL
        const intendedDestination = getIntendedDestination();
        clearStoredLoginRedirect();
        if (intendedDestination) {
          navigate(intendedDestination);
        } else {
          console.log('No intended destination, using default navigation');
          // Default navigation
          navigate(`/analytics/${userData.client_id}/DEFAULT/home`);
        }

    } else {
      showToast('Access Denied!', 'error');
    }
  }

  const handleGoogleLogin = useGoogleLogin({
          redirectUri: 'https://anthropod.in/dashboard/oauth2callback',
          onSuccess: async (code) => {
              const { access_token } = code;
              try {
                  let response = await server.googleLogin(access_token);
                  let resp = await response.json();
                  if(response.status === 200){
                      handleApiCheck(resp.info, resp.services, resp.session_id, resp.access_token_expires_at);
                  }else{
                    showToast(resp.error, 'error');
                  }
              } catch (error) {
                  console.error('Error logging in:', error);
                  showToast('Some error occured.', 'error');
              }
          },
          onError: (error) => {
              console.error('Login Failed:', error);
              showToast('Some error occured.', 'error');
          },
      });
      const handleMicrosoftLogin = () => {
        if (!microsoftClientId) {
          showToast('Microsoft login is not configured.', 'error');
          return;
        }
        persistLoginRedirect(location.search);
        const params = new URLSearchParams({
          client_id: microsoftClientId,
          response_type: 'code',
          redirect_uri: msRedirectUri(),
          response_mode: 'query',
          scope: 'openid profile email User.Read',
          state: 'ms_login',
        });
        window.location.href = `https://login.microsoftonline.com/${msAuthority()}/oauth2/v2.0/authorize?${params.toString()}`;
      };
      useEffect(() => {
        const q = new URLSearchParams(location.search);
        const code = q.get('code');
        if (!code || q.get('state') !== 'ms_login') return;

        const dedupeKey = `ms_handled_${code}`;
        if (sessionStorage.getItem(dedupeKey)) return;
        sessionStorage.setItem(dedupeKey, '1');

        navigate(`${location.pathname}${msStripOAuthParams(location.search)}`, { replace: true });

        (async () => {
          try {
            const response = await server.microsoftLogin(code);
            const resp = await response.json();
            if (response.status === 200) {
              handleApiCheck(resp.info, resp.services, resp.session_id, resp.access_token_expires_at);
            } else {
              showToast(resp.error || 'Some error occured.', 'error');
            }
          } catch (error) {
            console.error('Microsoft login failed:', error);
            showToast(error.message || 'Some error occured.', 'error');
          }
        })();
      }, [location.search]);
      const handleShowCreateLogin = (e) => {
        setShowCreateNewLogin(e)
      }

  return (
    <div className={`${isMobile ? '' : 'flex'}`}>
      <div style={{width: "50%", background: 'black', display: (!isMobile) ? 'flex':'none', alignItems: 'center', flexDirection: "column"}}>
        <div style={{height: "6rem", display: "flex", justifyContent: "center", alignItems: "center"}}><img src="/dashboard/assets/anthropod_logo_on_black_bg.svg" style={{height: "3.5rem", marginLeft: "1rem"}} alt=""/></div>
        <div style={{marginTop: "12rem"}}>
        <h2 style={{color: 'white', fontSize: '51px', fontWeight: "600", textAlign: "center"}}>Embrace the power of <br/> AI Analytics</h2>
        <div style={{ fontSize: '30px', textAlign: 'center', marginTop: '20px', color: 'grey' }}>
                {displayText}
            </div>
        </div>
      </div>
      <div className="flex items-center min-h-screen" style={{ background: 'white', flexDirection: "column", width: (!isMobile) ? "50%":"100%", justifyContent: (loading) ? 'center':'' }}>
      {/* <ToastContainer /> */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: "column" }}>
          <div className="loader"></div>
          <div style={{textAlign: "center", color: 'black'}}>Loading <br/> Please Wait</div>
        </div>
      ) : (
        <div className="text-center" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: "column", marginTop: (!isMobile) ? "15rem":"1rem"}}>
          {
            isMobile && (
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: "column", marginBottom: "9rem"}}>
                <img src="/dashboard/assets/anthro_logo_1.svg" style={{height: "3.5rem"}} alt=""/>
              </div>
            )
          }
          <h2 style={{color: 'black', fontSize: (!isMobile) ? "51px":"31px", fontWeight: "600", textAlign: "center"}}>Welcome to your <br/> Dashboard</h2>
          <div style={{gap: '1rem', marginTop: (!isMobile) ? "20px":"50px", width: "100%"}}>
            <button onClick={handleGoogleLogin} className="flex items-center bg-black text-white border border-gray-300 rounded-lg p-3 transition-transform transform hover:border-gray-500 hover:shadow-md hover:scale-105 shadow-md" style={{border: "1px solid black", width: "100%", justifyContent: "center"}}>
              <FcGoogle className="mr-2" />
              Sign in with Google
            </button>
            <button onClick={handleMicrosoftLogin} className="flex items-center bg-black text-white border border-gray-300 rounded-lg p-3 transition-transform transform hover:border-gray-500 hover:shadow-md hover:scale-105 shadow-md" style={{border: "1px solid black", width: "100%", justifyContent: "center", marginTop: "15px"}}>
              <FaMicrosoft className="mr-2" />
              Sign in with Microsoft
            </button>
            <button onClick={()=>setShowLogin(true)} style={{border: "1px solid black", borderRadius: "10px", padding: "10px 10px", fontSize: "16px", fontWeight: "600", cursor: "pointer", width: "12rem", marginTop: (isMobile) ? "1rem":"", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", marginTop: "15px"}}>
            <HiOutlineMail className="mr-2" style={{fontSize: "25px"}} />
              Sign in with Email
            </button>
            <div style={{display: "flex", alignItems: "center", marginTop: "15px"}}>
              <hr style={{width: "50%", border: "1px solid #d3d3d35e"}}/>
              <span style={{margin: "0px 5px", color: "grey"}}>or</span>
              <hr style={{width: "50%", border: "1px solid #d3d3d35e"}}/>
            </div>
            <button onClick={()=>setShowCreateNewLogin(true)} style={{border: "1px solid black", borderRadius: "10px", padding: "10px 10px", fontSize: "16px", fontWeight: "600", cursor: "pointer", width: "12rem", marginTop: (isMobile) ? "1rem":"", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", marginTop: "15px"}}>
            <TiUserAdd className="mr-2" style={{fontSize: "25px"}} />
              Create New Account
            </button>
          </div>
        </div>
      )}
    </div>
    {showLogin && (
        <ManualLogin login={handleLoginStatus}/>
      )}
      {
        showCreateNewLogin && (
          <CreateNewLogin showCreateLogin={handleShowCreateLogin}/>
        )
      }
    </div>
  );
};

export default Login;
